import { CallRouteV1 } from '@frontend/api-call-route';
import { i18next } from '@frontend/i18n';
import { IconName } from '@frontend/icons';

type StepOption = { type: CallRouteV1.Types.Node['type']; title: string; description: string; iconName: IconName };
export type StepOptions = {
  hoursAndAvailability: StepOption[];
  weaveAssistant: StepOption[];
  directRouting: StepOption[];
  messagingAndInformation: StepOption[];
};

export const STEP_OPTIONS: StepOptions = {
  hoursAndAvailability: [
    {
      type: CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS,
      title: i18next.t('Phone Hours', { ns: 'phone' }),
      description: i18next.t('Define hours in order to route calls differently by time of day.', { ns: 'phone' }),
      iconName: 'clock',
    },
  ],
  weaveAssistant: [
    {
      type: CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT,
      title: i18next.t('AI Receptionist', { ns: 'phone' }),
      description: i18next.t('Allow the AI Receptionist to help field your calls.', { ns: 'phone' }),
      iconName: 'aiweave-assistant',
    },
  ],
  directRouting: [
    {
      type: CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP,
      title: i18next.t('Call Group', { ns: 'phone' }),
      description: i18next.t('Multiple devices ring simultaneously or in cascading order.', { ns: 'phone' }),
      iconName: 'call-group',
    },
    {
      type: CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE,
      title: i18next.t('Call Queue', { ns: 'phone' }),
      description: i18next.t('Callers wait until your team is able to answer the call.', { ns: 'phone' }),
      iconName: 'call-queue',
    },
    {
      type: CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE,
      title: i18next.t('Phone Tree', { ns: 'phone' }),
      description: i18next.t('Give your callers options to choose from then define the call routing.', { ns: 'phone' }),
      iconName: 'phone-tree',
    },
    {
      type: CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE,
      title: i18next.t('Device', { ns: 'phone' }),
      description: i18next.t('Directly ring a specific device.', { ns: 'phone' }),
      iconName: 'cell-phone',
    },
    {
      type: CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER,
      title: i18next.t('Forwarding Number', { ns: 'phone' }),
      description: i18next.t('Send the call to a phone number.', { ns: 'phone' }),
      iconName: 'send-forward',
    },
    {
      type: CallRouteV1.Types.NodeType.NODE_TYPE_REPEAT,
      title: i18next.t('Repeat Phone Tree', { ns: 'phone' }),
      description: i18next.t('Repeat a Phone Tree.', { ns: 'phone' }),
      iconName: 'automated-message',
    },
    {
      type: CallRouteV1.Types.NodeType.NODE_TYPE_CALL_ROUTE,
      title: i18next.t('Call Route', { ns: 'phone' }),
      description: i18next.t('Send the call to another Call Route.', { ns: 'phone' }),
      iconName: 'call-route',
    },
  ],
  messagingAndInformation: [
    {
      type: CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX,
      title: i18next.t('Voicemail', { ns: 'phone' }),
      description: i18next.t('Allow your callers to leave a voicemail.', { ns: 'phone' }),
      iconName: 'voicemail',
    },
    {
      type: CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE,
      title: i18next.t('Play Message', { ns: 'phone' }),
      description: i18next.t('Play an informative audio message to the caller.', { ns: 'phone' }),
      iconName: 'speaker',
    },
  ],
} as const;

/**
 * Returns the available step options for adding a node to a call route, filtered based on the
 * contextual position of the current node within the route graph.
 *
 * - `hoursAndAvailability`: Excludes the Phone Hours option when the node is already a descendant
 *   or ancestor of a Phone Hours node, or is a descendant of a Weave Assistant node.
 * - `weaveAssistant`: Excludes the AI Receptionist option when the node is already a descendant
 *   or ancestor of a Weave Assistant node, or has a Phone Hours descendant.
 * - `directRouting`: Excludes the Repeat Phone Tree option unless the node is a descendant of a
 *   Phone Tree node.
 * - `messagingAndInformation`: Always returns all options unchanged.
 */
export const getCallRouteStepOptions = ({
  isPhoneTreeDescendant,
  isPhoneHoursDescendant,
  isWeaveAssistantDescendant,
  isWeaveAssistantAncestor,
  isPhoneHoursAncestor,
}: {
  isPhoneTreeDescendant: boolean;
  isPhoneHoursAncestor: boolean;
  isPhoneHoursDescendant: boolean;
  isWeaveAssistantAncestor: boolean;
  isWeaveAssistantDescendant: boolean;
}): StepOptions => {
  return {
    hoursAndAvailability: STEP_OPTIONS.hoursAndAvailability.filter(
      // Omit the phone hours node type if the processing node is a phone hours node descendant or ancestor
      // or if the processing node is a descendant of a weave assistant node (has a weave assistant ancestor)
      (option) =>
        !(
          (isPhoneHoursDescendant || isPhoneHoursAncestor || isWeaveAssistantDescendant) &&
          option.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS
        )
    ),
    weaveAssistant: STEP_OPTIONS.weaveAssistant.filter(
      // Omit the weave assistant node type if the processing node is a descendant or ancestor of a weave assistant node
      // or if the node has a phone hours descendant
      (option) =>
        !(
          (isWeaveAssistantDescendant || isWeaveAssistantAncestor || isPhoneHoursAncestor) &&
          option.type === CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT
        )
    ),
    directRouting: STEP_OPTIONS.directRouting.filter(
      (option) =>
        // Omit the repeat node type if the processing node is not a phone tree node descendant
        !(!isPhoneTreeDescendant && option.type === CallRouteV1.Types.NodeType.NODE_TYPE_REPEAT)
    ),
    messagingAndInformation: STEP_OPTIONS.messagingAndInformation,
  };
};

/**
 * Returns the restricted set of step options available when adding a node to a device extension
 * route.
 *
 * - `hoursAndAvailability`: Always empty — Phone Hours is not applicable to device extensions.
 * - `weaveAssistant`: Always empty — AI Receptionist is not applicable to device extensions.
 * - `directRouting`: Limited to Call Group, Device, and Forwarding Number.
 * - `messagingAndInformation`: Limited to Voicemail and Play Message.
 */
export const getDeviceExtensionRouteStepOptions = (): StepOptions => {
  return {
    // No options for hours and availability when adding a step to a device extension call route
    hoursAndAvailability: [],
    weaveAssistant: [],
    directRouting: STEP_OPTIONS.directRouting.filter((option) =>
      // Only allow these direct routing options for device extension call routes
      [
        CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP,
        CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE,
        CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER,
      ].includes(option.type)
    ),
    messagingAndInformation: STEP_OPTIONS.messagingAndInformation.filter((option) =>
      [CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX, CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE].includes(
        option.type
      )
    ),
  };
};
